import { useQuery } from '@tanstack/react-query';
import { UseQueryReturnType, UseQueryOptionsType } from 'app/utils/api/queries';
import { ListingReviewType, ListingsFormData, ListingType } from 'app/types';
import { removeHtmlTagsAndEntities } from 'app/utils/helpers';
import API from 'app/utils/api/axios';
import API_URL from 'app/constants/api_urls';
import { AxiosError } from 'axios';

const useVendorListingsFormData = (
    options?: UseQueryOptionsType
): UseQueryReturnType<ListingsFormData | undefined> => {
    const { isPending, isError, data, error } = useQuery({
        queryKey: ['vendor_listings_form_data'],
        queryFn: async () => {
            const { data } = await API.get<ListingsFormData>(API_URL.BUSINESS_LISTINGS_FORM_DATA);
            return data;
        },
        ...options,
    });

    return {
        data,
        isLoading: isPending,
        isError,
        error,
    };
};

const useVendorListings = (
    options?: UseQueryOptionsType
): UseQueryReturnType<ListingType[] | undefined> => {
    const { isPending, isLoading, isError, data, error } = useQuery({
        queryKey: ['vendor_listings'],
        queryFn: async () => {
            const { data } = await API.get<ListingType[]>(API_URL.BUSINESS_LISTINGS);
            return data;
        },
        ...options,
    });

    return {
        data,
        isLoading: isPending && isLoading,
        isError,
        error,
    };
};

const useVendorListingDetails = (
    id: string | number,
    options?: UseQueryOptionsType
): UseQueryReturnType<ListingType | any> => {
    const { isLoading, isError, data, error } = useQuery({
        queryKey: ['vendor_listing_details', id],
        queryFn: async () => {
            const { data } = await API.get(`${API_URL.BUSINESS_LISTINGS}${id}`);
            return {
                ...data,
                description: removeHtmlTagsAndEntities(data?.description),
            };
        },

        ...options,
    });

    return {
        data,
        isLoading,
        isError,
        error,
    };
};

const useListingSummary = (
    pk: string | number,
    options?: UseQueryOptionsType
): UseQueryReturnType<Pick<ListingType, 'pk' | 'title' | 'uuid'> | undefined> => {
    const { isPending, isError, data, error } = useQuery({
        queryKey: ['listing_summary', pk],
        queryFn: async () => {
            const { data } = await API.get(`${API_URL.BUSINESS_LISTINGS}${pk}/summary/`);
            return data;
        },
        retry(failureCount, error) {
            if ((error as AxiosError).response?.status === 404) return false;
            return failureCount < 3;
        },
        ...options,
    });

    return {
        data,
        isLoading: isPending,
        isError,
        error,
    };
};

const useUserListingReviews = (
    args: {
        pk: string | number;
        userPk: string | number;
    },
    options?: UseQueryOptionsType
): UseQueryReturnType<ListingReviewType[] | undefined> => {
    const { pk, userPk } = args;

    const { isPending, isError, data, error } = useQuery({
        queryKey: ['listing_reviews', pk, userPk],
        queryFn: async () => {
            const { data } = await API.get(
                `${API_URL.BUSINESS_LISTINGS}${pk}/reviews?user=${userPk}`
            );
            return data;
        },
        ...options,
    });

    return {
        data,
        isLoading: isPending,
        isError,
        error,
    };
};

export {
    useVendorListings,
    useVendorListingDetails,
    useVendorListingsFormData,
    useListingSummary,
    useUserListingReviews,
};
